import React, { useCallback, useEffect, useState } from "react";
import {
  Box, Button, Chip, FormControl, Grid, InputLabel, MenuItem,
  Paper, Select, TextField, Typography,
} from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import SendIcon from "@mui/icons-material/Send";
import api from "../../api";
import { toast } from "react-toastify";
import AnalyticsPageShell, { CARD_SX } from "./AnalyticsPageShell";
import AnalyticsFiltersBar from "./AnalyticsFiltersBar";
import useAnalyticsFilters from "./useAnalyticsFilters";
import AnalyticsDataTable, { StatusChip } from "./AnalyticsDataTable";

const TILE_CONFIGS = [
  { key: "underperforming_students", label: "Underperforming Students", Icon: PersonIcon,       iconBg: "#ede9fe", iconColor: "#7c3aed" },
  { key: "missed_weekly_work",       label: "Missed Weekly Work",       Icon: CalendarMonthIcon, iconBg: "#dcfce7", iconColor: "#16a34a" },
  { key: "low_score_alerts",         label: "Low Score Alerts",         Icon: WarningAmberIcon,  iconBg: "#fff7ed", iconColor: "#ea580c" },
];

function KpiTile({ label, value, Icon, iconBg, iconColor }) {
  return (
    <Paper sx={{ ...CARD_SX, display: "flex", alignItems: "center", gap: 2 }}>
      <Box sx={{ width: 52, height: 52, borderRadius: "50%", bgcolor: iconBg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
        <Icon sx={{ color: iconColor, fontSize: 26 }} />
      </Box>
      <Box>
        <Typography variant="body2" color="text.secondary">{label}</Typography>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>{(value ?? 0).toLocaleString()}</Typography>
      </Box>
    </Paper>
  );
}

const AVATAR_COLORS = ["#7c3aed", "#2563eb", "#16a34a", "#d97706", "#dc2626", "#0891b2"];

function StudentAvatar({ name, index }) {
  const initials = name
    ? name.split(" ").slice(0, 2).map((n) => n[0]).join("").toUpperCase()
    : "?";
  const bg = AVATAR_COLORS[index % AVATAR_COLORS.length];
  return (
    <Box sx={{ width: 28, height: 28, borderRadius: "50%", bgcolor: bg, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, flexShrink: 0 }}>
      {initials}
    </Box>
  );
}

export default function AcademicSupportAnalytics() {
  const [courseId, setCourseId] = useState("");
  const [academicCourseId, setAcademicCourseId] = useState("");
  const [planId, setPlanId] = useState("");
  const [alertLevel, setAlertLevel] = useState("");
  const [summary, setSummary] = useState({});
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const [recipient, setRecipient] = useState("");
  const [weakAreas, setWeakAreas] = useState([]);
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  const { courses, academicCourses, plans } = useAnalyticsFilters({ courseId, academicCourseId });

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (courseId) params.course_id = courseId;
      if (academicCourseId) params.academic_course_id = academicCourseId;
      if (planId) params.course_plan_id = planId;
      if (alertLevel) params.alert_level = alertLevel;
      const res = await api.get("admin/analytics/academic-support", { params });
      const d = res.data?.data || {};
      setSummary(d.summary || {});
      setRows(d.rows || []);
    } catch (e) {
      toast.error(e.response?.data?.message || "Failed to load academic support list");
      setSummary({});
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [courseId, academicCourseId, planId, alertLevel]);

  useEffect(() => { load(); }, [load]);

  const recipientRow = rows.find((r) => String(r.student_id) === String(recipient));
  const allWeakAreas = [...new Set(rows.map((r) => r.weak_area).filter(Boolean))];

  const sendNotify = async () => {
    if (!recipientRow?.parent_email) return;
    try {
      setSending(true);
      await api.post("admin/analytics/academic-support/notify", {
        student_id: recipientRow.student_id,
        parent_email: recipientRow.parent_email,
        weak_areas: weakAreas.length ? weakAreas : [recipientRow.weak_area].filter(Boolean),
        batch_name: recipientRow.batch_name,
        message,
      });
      toast.success("Notification sent");
      setRecipient("");
      setWeakAreas([]);
      setMessage("");
    } catch (e) {
      toast.error(e.response?.data?.message || "Failed to send");
    } finally {
      setSending(false);
    }
  };

  const columns = [
    {
      key: "student_name",
      label: "Student",
      render: (r, idx) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <StudentAvatar name={r.student_name} index={idx} />
          <span>{r.student_name}</span>
        </Box>
      ),
    },
    { key: "batch_name", label: "Group" },
    { key: "missed_work_count", label: "Missed Weekly Work" },
    {
      key: "average_mark_percent",
      label: "Average Mark",
      render: (r) => {
        const pct = r.average_mark_percent;
        if (pct == null) return "—";
        const color = pct < 40 ? "#dc2626" : pct < 50 ? "#d97706" : "inherit";
        return <Typography variant="body2" sx={{ color, fontWeight: pct < 50 ? 700 : 400 }}>{pct}%</Typography>;
      },
    },
    { key: "weak_area", label: "Weak Area" },
    { key: "parent_email", label: "Parent Email" },
    { key: "alert_level", label: "Alert Level", render: (r) => <StatusChip status={r.alert_level} /> },
  ];

  const parentRows = rows.filter((r) => r.parent_email);

  return (
    <AnalyticsPageShell
      title="Underperforming Students & Parent Alerts"
      subtitle="Identify students who missed weekly work or got low marks to take timely action."
      filters={
        <AnalyticsFiltersBar
          courses={courses}
          academicCourses={academicCourses}
          plans={plans}
          courseId={courseId}
          setCourseId={setCourseId}
          showAcademicCourse={false}
          academicCourseId={academicCourseId}
          setAcademicCourseId={setAcademicCourseId}
          planId={planId}
          setPlanId={setPlanId}
          showAlertLevel
          alertLevel={alertLevel}
          setAlertLevel={setAlertLevel}
        />
      }
    >
      <Grid container spacing={2} sx={{ mb: 2 }}>
        {TILE_CONFIGS.map(({ key, label, Icon, iconBg, iconColor }) => (
          <Grid item xs={12} sm={4} key={key}>
            <KpiTile label={label} value={summary[key]} Icon={Icon} iconBg={iconBg} iconColor={iconColor} />
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12} md={8}>
          <AnalyticsDataTable columns={columns} rows={rows} loading={loading} />
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper sx={{ ...CARD_SX, position: "sticky", top: 16 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>Parent Notification</Typography>
            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
              <InputLabel>Recipient</InputLabel>
              <Select label="Recipient" value={recipient} onChange={(e) => setRecipient(e.target.value)}>
                <MenuItem value="">Select Parent</MenuItem>
                {parentRows.map((r) => (
                  <MenuItem key={r.student_id} value={r.student_id}>
                    {r.student_name} — {r.parent_email}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
              <InputLabel>Weak Areas</InputLabel>
              <Select
                multiple
                label="Weak Areas"
                value={weakAreas}
                onChange={(e) => setWeakAreas(typeof e.target.value === "string" ? e.target.value.split(",") : e.target.value)}
                renderValue={(sel) => (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {sel.map((v) => <Chip key={v} label={v} size="small" />)}
                  </Box>
                )}
              >
                {allWeakAreas.map((area) => (
                  <MenuItem key={area} value={area}>{area}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              fullWidth
              multiline
              minRows={4}
              size="small"
              label="Message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Select a parent to view and edit the notification message."
              sx={{ mb: 2 }}
            />
            <Button
              fullWidth
              variant="contained"
              startIcon={<SendIcon />}
              disabled={!recipientRow?.parent_email || sending}
              onClick={sendNotify}
              sx={{ textTransform: "none" }}
            >
              Send Notification
            </Button>
          </Paper>
        </Grid>
      </Grid>
    </AnalyticsPageShell>
  );
}
